import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import TagInput from "../components/TagInput";
import { API, api, formatApiErrorDetail, getToken } from "../lib/api";

const SENIORITY = ["junior", "confirmé", "senior", "expert"];
const REMOTE = ["onsite", "hybrid", "remote"];
const LANGS = ["fr", "en", "es", "de", "it"];
const SORTS = ["newest", "tjm_asc", "tjm_desc"];

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function Cvtheque() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    q: "",
    skills: [],
    seniority: "",
    remote_policy: "",
    tjm_min: "",
    tjm_max: "",
    languages: [],
    available_from: "",
    location: "",
    distance_km: 50,
  });
  const [sort, setSort] = useState("newest");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { sort };
    if (filters.q) p.q = filters.q;
    if (filters.skills.length) p.skills = filters.skills.join(",");
    if (filters.seniority) p.seniority = filters.seniority;
    if (filters.remote_policy) p.remote_policy = filters.remote_policy;
    if (filters.tjm_min) p.tjm_min = filters.tjm_min;
    if (filters.tjm_max) p.tjm_max = filters.tjm_max;
    if (filters.languages.length) p.languages = filters.languages.join(",");
    if (filters.available_from) p.available_from = filters.available_from;
    if (filters.location) {
      p.location = filters.location;
      p.distance_km = filters.distance_km;
    }
    return p;
  }, [filters, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cvtheque/consultants", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  }, [params, t]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const canDistance = !!filters.location;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cvtheque-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
              {t("cvtheque.eyebrow")}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t("cvtheque.title")}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("cvtheque.subtitle")}</p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">{t("marketplace.sort_by")}</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="cvtheque-sort"
            >
              {SORTS.map((s) => (
                <option key={s} value={s}>{t(`cvtheque.sort_${s}`)}</option>
              ))}
              <option value="distance" disabled={!canDistance}>{t("cvtheque.sort_distance")}</option>
            </select>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[280px_1fr]">
          <aside className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="cvtheque-filters">
            <F label={t("cvtheque.f_search")}>
              <input value={filters.q} onChange={(e) => setF("q", e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-q" />
            </F>
            <F label={t("cvtheque.f_skills")}>
              <TagInput value={filters.skills} onChange={(v) => setF("skills", v)} placeholder={t("roster.skills_placeholder")} testId="cv-filter-skills" containerTestId="cv-filter-skills-container" />
            </F>
            <F label={t("cvtheque.f_seniority")}>
              <select value={filters.seniority} onChange={(e) => setF("seniority", e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-seniority">
                <option value="">{t("cvtheque.f_any")}</option>
                {SENIORITY.map((s) => <option key={s} value={s}>{t(`roster.seniority_${s}`)}</option>)}
              </select>
            </F>
            <F label={t("cvtheque.f_remote")}>
              <select value={filters.remote_policy} onChange={(e) => setF("remote_policy", e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-remote">
                <option value="">{t("cvtheque.f_any")}</option>
                {REMOTE.map((r) => <option key={r} value={r}>{t(`roster.remote_${r}`)}</option>)}
              </select>
            </F>
            <F label={t("cvtheque.f_tjm")}>
              <div className="flex gap-2">
                <input type="number" min={0} value={filters.tjm_min} onChange={(e) => setF("tjm_min", e.target.value)} placeholder={t("cvtheque.f_min")} className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-tjm-min" />
                <input type="number" min={0} value={filters.tjm_max} onChange={(e) => setF("tjm_max", e.target.value)} placeholder={t("cvtheque.f_max")} className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-tjm-max" />
              </div>
            </F>
            <F label={t("cvtheque.f_languages")}>
              <div className="flex flex-wrap gap-1.5">
                {LANGS.map((l) => {
                  const active = filters.languages.includes(l);
                  return (
                    <button
                      type="button"
                      key={l}
                      onClick={() => setF("languages", active ? filters.languages.filter((x) => x !== l) : [...filters.languages, l])}
                      data-testid={`cv-filter-lang-${l}`}
                      className={
                        "rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wider " +
                        (active ? "bg-indigo-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100")
                      }
                    >{l}</button>
                  );
                })}
              </div>
            </F>
            <F label={t("cvtheque.f_available_from")}>
              <input type="date" value={filters.available_from} onChange={(e) => setF("available_from", e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-available" />
            </F>
            <F label={t("marketplace_extra.f_location")}>
              <input value={filters.location} onChange={(e) => setF("location", e.target.value)} placeholder={t("marketplace_extra.f_location_placeholder")} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20" data-testid="cv-filter-location" />
            </F>
            <F label={`${t("marketplace_extra.f_radius")} (${filters.distance_km} ${t("marketplace_extra.distance_hint")})`}>
              <input type="range" min={5} max={200} step={5} value={filters.distance_km} onChange={(e) => setF("distance_km", Number(e.target.value))} disabled={!filters.location} className="w-full disabled:opacity-40" data-testid="cv-filter-radius" />
            </F>
            <button type="button" onClick={() => setFilters({ q: "", skills: [], seniority: "", remote_policy: "", tjm_min: "", tjm_max: "", languages: [], available_from: "", location: "", distance_km: 50 })} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="cv-filter-reset">
              {t("cvtheque.reset")}
            </button>
          </aside>

          <section>
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">{t("common.loading")}</div>
            )}
            {!loading && items.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-sm" data-testid="cvtheque-empty">
                <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500 ring-1 ring-indigo-100">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-900">{t("cvtheque.empty_title")}</h3>
                <p className="mt-2 text-sm text-slate-600">{t("cvtheque.empty_body")}</p>
              </div>
            )}
            {!loading && items.length > 0 && (
              <ul className="grid gap-4" data-testid="cvtheque-list">
                {items.map((c) => (<Card key={c.id} c={c} onCVDownload={() => downloadCv(c.id, t)} />))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

async function downloadCv(id, t) {
  try {
    const res = await fetch(`${API}/cvtheque/consultants/${id}/cv`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
  } catch (_) {
    toast.error(t("common.error_generic"));
  }
}

function F({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function Card({ c, onCVDownload }) {
  const { t } = useTranslation();
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={`cvtheque-card-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-[280px]">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/cvtheque/${c.id}`} className="text-base font-semibold text-slate-900 hover:text-indigo-700 md:text-lg" data-testid={`cvtheque-card-name-${c.id}`}>
              {c.display_name}
            </Link>
            {c.redact_identity && (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600 ring-1 ring-slate-200">
                🔒 {t("cvtheque.identity_hidden")}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-slate-600">{c.title}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <AgencyBadge orgId={c.agency_org_id} name={c.agency_name} hasLogo={c.agency_has_logo} />
            <span>·</span>
            <span>{t(`roster.seniority_${c.seniority}`)}</span>
            <span>·</span>
            <span>{c.years_experience} {t("cvtheque.years_short")}</span>
            <span>·</span>
            <span>{t(`roster.remote_${c.remote_policy}`)}</span>
            {c.location_city && (<><span>·</span><span>{c.location_city}</span></>)}
            {typeof c.distance_km === "number" && (<><span>·</span><span>{c.distance_km} km</span></>)}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(c.skills || []).slice(0, 5).map((s) => (
              <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{s}</span>
            ))}
            {c.skills && c.skills.length > 5 && (
              <span className="text-xs text-slate-500">+{c.skills.length - 5}</span>
            )}
          </div>
          {c.bio_excerpt && <p className="mt-3 line-clamp-2 text-sm text-slate-600">{c.bio_excerpt}</p>}
        </div>
        <div className="text-right text-xs text-slate-600">
          <div className="text-lg font-semibold text-slate-900">{c.tjm_target}/day</div>
          <div className="mt-1">{t("roster.availability")} {fmtDate(c.availability_date)}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={onCVDownload}
          disabled={!c.has_cv}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`cvtheque-download-btn-${c.id}`}
        >
          {c.has_cv ? t("cvtheque.download_cv") : t("cvtheque.no_cv")}
        </button>
        <Link
          to={`/cvtheque/${c.id}?contact=1`}
          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
          data-testid={`cvtheque-contact-btn-${c.id}`}
        >
          {t("cvtheque.contact_agency")}
        </Link>
      </div>
    </li>
  );
}

function AgencyBadge({ orgId, name, hasLogo }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {hasLogo ? (
        <img src={`${API}/orgs/${orgId}/logo`} alt="" className="h-4 w-4 rounded-sm object-cover" />
      ) : (
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-slate-200 text-[9px] font-bold text-slate-600">
          {(name || "").slice(0, 1).toUpperCase()}
        </span>
      )}
      <span>{name}</span>
    </span>
  );
}
